import { t } from '../../../../../common/i18n.js'
import { Language } from '../../../../../common/types/i18n.js'
import { formatDisplayDate } from '../../../../../common/utils/format-utils.js'
import { ExternalLink, FileText, Folder, Link2 } from '../../../../components/icon/solar-icons.js'
import { PortalEmptyState } from '../../../../components/portal-empty-state/portal-empty-state.js'
import { isGoogleDocsUrl, isGoogleDriveUrl, parseDriveFolderId } from '../../utils/portal-api.js'
import type { EmployeeDocument } from '../employee-profile/employee-profile-types.js'
import './documents-view.css'

interface DocumentListProps {
    documents: EmployeeDocument[]
    lang: Language
    /** When provided, Drive-folder rows browse inline instead of opening a new tab. */
    onOpenFolder?: (doc: EmployeeDocument) => void
}

type DocLink =
    | { kind: 'file'; href: string }
    | { kind: 'folder'; href: string }
    | { kind: 'docs'; href: string }
    | { kind: 'drive'; href: string }
    | { kind: 'external'; href: string }
    | { kind: 'none' }

const resolveLink = (doc: EmployeeDocument): DocLink => {
    if (doc.file_url) return { kind: 'file', href: doc.file_url }
    const url = doc.drive_url
    if (!url) return { kind: 'none' }
    if (parseDriveFolderId(url)) return { kind: 'folder', href: url }
    if (isGoogleDocsUrl(url)) return { kind: 'docs', href: url }
    if (isGoogleDriveUrl(url)) return { kind: 'drive', href: url }
    return { kind: 'external', href: url }
}

const rowDate = (doc: EmployeeDocument): string =>
    (doc.issue_date ?? doc.created_at_utc)?.slice(0, 10)

/**
 * Read-only list of an employee's documents (rows in
 * `portal_employee_documents`), sorted newest-first. Stored files link to the
 * authenticated download route; Google Docs/Drive links open in a new tab. When
 * `onOpenFolder` is given, Drive-folder rows browse inline (used on the self
 * page once Drive is connected) instead of opening externally.
 */
export const DocumentList = ({ documents, lang, onOpenFolder }: DocumentListProps) => {
    const d = t.portal.documents

    if (documents.length === 0) {
        return <PortalEmptyState icon={<FileText size={32} />} message={d.noDocuments(lang)} />
    }

    const sorted = [...documents].sort((a, b) => rowDate(b).localeCompare(rowDate(a)))

    return (
        <ul className="portal-documents__list">
            {sorted.map((doc) => (
                <DocumentRow key={doc.id} doc={doc} lang={lang} onOpenFolder={onOpenFolder} />
            ))}
        </ul>
    )
}

const DocumentRow = ({
    doc,
    lang,
    onOpenFolder,
}: {
    doc: EmployeeDocument
    lang: Language
    onOpenFolder?: (doc: EmployeeDocument) => void
}) => {
    const d = t.portal.documents
    const link = resolveLink(doc)
    const isFolder = link.kind === 'folder'
    const Icon = link.kind === 'file' ? FileText : isFolder ? Folder : Link2
    const date = rowDate(doc)

    const label =
        link.kind === 'docs'
            ? d.viewInGoogleDocs(lang)
            : link.kind === 'drive' || (isFolder && !onOpenFolder)
              ? d.viewInGoogleDrive(lang)
              : d.view(lang)

    // Inline-browse a Drive folder when the page supports it; otherwise the
    // action is a plain external/download link.
    const action =
        link.kind === 'none' ? null : isFolder && onOpenFolder ? (
            <button
                type="button"
                className="portal-documents__doc-action"
                onClick={() => onOpenFolder(doc)}
            >
                {d.view(lang)}
            </button>
        ) : (
            <a
                className="portal-documents__doc-action"
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
            >
                {label}
                <ExternalLink size={13} />
            </a>
        )

    return (
        <li className="portal-documents__doc-row">
            <Icon size={18} className="portal-documents__doc-icon" />
            <div className="portal-documents__doc-info">
                <span className="portal-documents__doc-name">{doc.name}</span>
                {date && (
                    <span className="portal-documents__doc-date">
                        {formatDisplayDate(date, 'long', lang)}
                    </span>
                )}
            </div>
            {action}
        </li>
    )
}
